import { useMemo } from 'react';

import { AutoLinkContext } from '@betterment/js-runtime';
import { Box } from '@betterment/samba-react';
import { ErrorBoundary, useToastCookie } from '@betterment/shared-components';
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { ParticipantSelectionStepProps } from '../components/Household/ParticipantSelectionStep';
import { Pending401kBanner } from '../components/Household/Pending401kBanner';
import { Spouse401kBanner } from '../components/Household/Spouse401kBanner';
import { getRoutes } from '../components/InstitutionalApp/Routes';
import NotFound from '../components/NotFound';
import Portal from '../components/Portal';
import { ImpersonationModalProvider } from '../contexts/ImpersonationModalContext';
import { GraphQLProvider } from '../providers/GraphQLProvider';
import urlBuilder from '../Shared/url-builder';

interface Props {
  isSingleUserHousehold?: boolean;
  partcipantSelectionProps?: ParticipantSelectionStepProps;
}

function Root() {
  useToastCookie();

  return (
    <ErrorBoundary fallbackType="full">
      <Outlet />
    </ErrorBoundary>
  );
}

export function InstitutionalApp({ isSingleUserHousehold, partcipantSelectionProps }: Props) {
  const autoLinkContextValue = useMemo(
    () => ({
      routes: getRoutes(isSingleUserHousehold, partcipantSelectionProps),
      basename: '/',
    }),
    [isSingleUserHousehold, partcipantSelectionProps]
  );

  // This is temporary until we fully migrate to React Household Summary and get rid of the portals
  const emptyHouseholdRoute = useMemo(() => {
    return {
      path: urlBuilder.getHouseholdSummaryPage.absolutePath,
      element: <Box />,
    };
  }, []);

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/',
          element: <Root />,
          children: [...autoLinkContextValue.routes, emptyHouseholdRoute, { path: '*', element: <NotFound /> }],
        },
      ]),
    [autoLinkContextValue.routes, emptyHouseholdRoute]
  );

  return (
    <ImpersonationModalProvider>
      <GraphQLProvider>
        <AutoLinkContext.Provider value={autoLinkContextValue}>
          <Portal app="Institutional" name="Spouse401kBanner">
            <Spouse401kBanner />
          </Portal>
          <Portal app="Institutional" name="Pending401kBanner">
            <Pending401kBanner />
          </Portal>
          <RouterProvider router={router} />
        </AutoLinkContext.Provider>
      </GraphQLProvider>
    </ImpersonationModalProvider>
  );
}

export default InstitutionalApp;
